// src/AdminPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  doc,
} from "firebase/firestore";

import AdminSidebar from "./components/AdminSidebar";
import AdminProductsPage from "./AdminProductsPage";

import { useTheme } from "./contexts/ThemeContext";
import { useUser } from "./contexts/UserContext";
import { useNavigate, Link } from "react-router-dom";

export default function AdminPage() {
  const navigate = useNavigate();
  const { userData, loading } = useUser();
  const { darkMode } = useTheme();

  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  /** Admin Access Protection */
  useEffect(() => {
    if (!loading && (!userData || userData.role !== "admin")) {
      navigate("/", { replace: true });
    }
  }, [userData, loading, navigate]);

  /** Live Orders Listener */
  useEffect(() => {
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const normalized = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        status: d.data().status || "Pending",
      }));
      setOrders(normalized);
    });
    return () => unsub();
  }, []);

  /** Stats */
  const stats = useMemo(() => {
    const totalOrders = orders.length;
    // Use grandTotal if available, else fallback to total
    const totalRevenue = orders.reduce((s, o) => s + (o.grandTotal || o.total || 0), 0);
    return {
      totalOrders,
      totalRevenue,
      pending: orders.filter((o) => o.status === "Pending").length,
      delivered: orders.filter((o) => o.status === "Delivered").length,
    };
  }, [orders]);

  /** Change Status */
  async function changeStatus(orderDocId, nextStatus) {
    try {
      setUpdatingOrderId(orderDocId);
      await updateDoc(doc(db, "orders", orderDocId), {
        status: nextStatus,
      });
    } finally {
      setUpdatingOrderId(null);
    }
  }

  function formatDate(ts) {
    if (!ts) return "-";
    return ts.toDate
      ? ts.toDate().toLocaleString()
      : new Date(ts).toLocaleString();
  }

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen flex bg-gradient-to-b from-pink-100 to-white dark:from-gray-900 dark:to-gray-800 text-gray-900 dark:text-gray-200">

        {/* SIDEBAR */}
        <div
          className={`
            fixed md:relative z-40 h-full md:h-auto 
            transform transition-transform duration-300
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          `}
        >
          <AdminSidebar
            activeTab={activeTab}
            setActiveTab={(tab) => {
              setActiveTab(tab);
              setSidebarOpen(false);
            }}
          />
        </div>

        {/* BACKDROP FOR MOBILE */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 md:hidden z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* MAIN CONTENT */}
        <main className="flex-1 p-6 max-w-6xl mx-auto">

          {/* HEADER */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                className="md:hidden p-2 rounded bg-pink-600 text-white shadow"
                onClick={() => setSidebarOpen(true)}
              >
                ☰
              </button>
              <h1 className="text-3xl font-extrabold text-pink-600 dark:text-pink-300">
                Admin Dashboard
              </h1>
            </div>

            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 rounded-lg bg-white dark:bg-gray-800 shadow text-sm font-semibold text-pink-600 dark:text-pink-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              ⟵ Back to Home
            </button>
          </div>

          {/* DASHBOARD TAB */}
          {activeTab === "dashboard" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 shadow">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Orders</div>
                  <div className="text-3xl font-bold text-pink-600 dark:text-pink-300">{stats.totalOrders}</div>
                </div>
                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 shadow">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Revenue</div>
                  <div className="text-3xl font-bold text-pink-600 dark:text-pink-300">
                    ₹{stats.totalRevenue}
                  </div>
                </div>
                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 shadow">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Pending</div>
                  <div className="text-3xl font-bold text-yellow-500">{stats.pending}</div>
                </div>
                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 shadow">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Delivered</div>
                  <div className="text-3xl font-bold text-green-500">{stats.delivered}</div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow">
                <h2 className="text-xl font-semibold dark:text-gray-100 mb-3">Recent Orders</h2>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">No orders yet.</div>
                ) : (
                  <div className="space-y-3">
                    {orders.slice(0, 6).map((o) => (
                      <div
                        key={o.id}
                        className="p-3 rounded-xl bg-pink-50 dark:bg-gray-700 flex justify-between items-center"
                      >
                        <div>
                          <div className="font-semibold dark:text-gray-100">{o.customer?.name}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">
                            {o.orderNumber} • ₹{o.grandTotal || o.total} • {formatDate(o.createdAt)}
                          </div>
                        </div>

                        <Link
                          to={`/admin/order/${o.id}`}
                          className="px-4 py-2 rounded-lg bg-white dark:bg-gray-800 shadow text-sm font-semibold text-pink-600 dark:text-pink-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          View Order
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ORDERS TAB */}
          {activeTab === "orders" && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-5">
                <h2 className="text-xl font-semibold dark:text-gray-100 mb-4">All Orders</h2>

                {orders.map((o) => (
                  <div
                    key={o.id}
                    className="p-4 rounded-xl bg-white dark:bg-gray-700 mb-4 shadow border dark:border-gray-700"
                  >
                    <div className="flex justify-between">
                      <div>
                        <div className="text-lg font-semibold text-pink-600 dark:text-pink-300">{o.customer?.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-300">
                          {o.customer?.email} • {o.customer?.mobile}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm dark:text-gray-300">Order</div>
                        <div className="font-mono text-pink-600 dark:text-pink-300">{o.orderNumber}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      {/* Items */}
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-300">Items</div>
                        <ul className="mt-1 space-y-1 text-sm dark:text-gray-200">
                          {o.items?.map((it, i) => (
                            <li key={i} className="flex justify-between">
                              <span>{it.name} × {it.qty}</span>
                              <span>₹{it.price}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Address */}
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-300">Address</div>
                        <div className="text-sm dark:text-gray-200">{o.customer?.address}</div>
                      </div>

                      {/* Status */}
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-300">Status</div>
                        <select
                          className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border dark:border-gray-600 dark:text-gray-200"
                          value={o.status}
                          onChange={(e) => changeStatus(o.id, e.target.value)}
                        >
                          <option>Pending</option>
                          <option>Shipped</option>
                          <option>Delivered</option>
                          <option>Cancelled</option>
                        </select>
                        {updatingOrderId === o.id && (
                          <div className="text-sm mt-1 text-gray-500 dark:text-gray-400">Updating…</div>
                        )}
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="mt-4 text-right text-lg font-bold text-pink-600 dark:text-pink-300 space-y-1">
                      <div>Subtotal: ₹{o.total}</div>
                      <div>Shipping: ₹{o.shippingCharge || 0}</div>
                      <div>Grand Total: ₹{o.grandTotal || o.total}</div>
                    </div>

                    <div className="mt-3 text-right">
                      <Link
                        to={`/admin/order/${o.id}`}
                        className="text-pink-600 dark:text-pink-300 font-semibold underline"
                      >
                        View Order Details →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PRODUCTS TAB */}
          {activeTab === "products" && <AdminProductsPage />}
        </main>
      </div>
    </div>
  );
}
